import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { ButtonLink } from './shared/ButtonLink';
import type { FaqComponent as FaqComponentType } from '../schemas/validation';
import { useThemeSafe } from '../contexts/ThemeContext';
import { useCustomColors, useBrandFonts } from '../contexts/CustomColorsContext';
import { getThemeStyles } from '../styles/themes';
import { renderTextWithLineBreaks } from '../utils/textFormatting';
import { getFontFamily } from '../utils/fontLoader';

interface FaqComponentProps {
  component: FaqComponentType;
}

export const FaqComponent: React.FC<FaqComponentProps> = ({ component }) => {
  const { data } = component;
  const { currentTheme } = useThemeSafe();
  const customColors = useCustomColors();
  const brandFonts = useBrandFonts();
  const themeStyles = getThemeStyles(currentTheme, customColors || undefined);

  // Font styles for headings and body text
  const headingFontStyle = brandFonts?.headingFont
    ? { fontFamily: getFontFamily(brandFonts.headingFont) }
    : {};
  const bodyFontStyle = brandFonts?.bodyFont
    ? { fontFamily: getFontFamily(brandFonts.bodyFont) }
    : {};

  // Track which items are open
  const [openItems, setOpenItems] = useState<Set<string>>(new Set());

  // Initialize with first item open if defaultOpenFirst is true
  useEffect(() => {
    if (data.defaultOpenFirst && data.items.length > 0) {
      setOpenItems(new Set([data.items[0].id]));
    }
  }, [data.defaultOpenFirst, data.items]);

  // Color priority: component.customColors > theme colors
  const backgroundColor = data.customColors?.background || themeStyles.faq?.background || '#f9fafb';
  const titleColor = data.customColors?.title || themeStyles.faq?.titleColor || '#111827';
  const subtitleColor = data.customColors?.subtitle || themeStyles.faq?.subtitleColor || '#6b7280';
  const questionColor =
    data.customColors?.questionText || themeStyles.faq?.questionColor || '#111827';
  const answerColor = data.customColors?.answerText || themeStyles.faq?.answerColor || '#4b5563';
  const itemBackground =
    data.customColors?.itemBackground || themeStyles.faq?.itemBackground || '#ffffff';
  const itemBorder = data.customColors?.itemBorder || themeStyles.faq?.itemBorder || '#e5e7eb';
  const iconColor = data.customColors?.iconColor || themeStyles.faq?.iconColor || '#3b82f6';

  // Button colors: component customColors > theme colors > fallback
  const buttonBgColor =
    data.customColors?.buttonBg ||
    themeStyles.faq?.buttonBg ||
    themeStyles.cta?.buttonBg ||
    '#3b82f6';
  const buttonTextColor =
    data.customColors?.buttonText ||
    themeStyles.faq?.buttonText ||
    themeStyles.cta?.buttonText ||
    '#ffffff';

  // Get button styles based on button type
  const getButtonStyles = (style: string) => {
    switch (style) {
      case 'primary':
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
      case 'secondary':
        return {
          backgroundColor: 'rgba(128, 128, 128, 0.1)',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      case 'outline':
        return {
          backgroundColor: 'transparent',
          color: buttonBgColor,
          border: `2px solid ${buttonBgColor}`,
        };
      default:
        return {
          backgroundColor: buttonBgColor,
          color: buttonTextColor,
          border: 'none',
        };
    }
  };

  // Render buttons helper
  const renderButtons = () => {
    if (!data.buttons || data.buttons.length === 0) return null;

    return (
      <div className="flex flex-wrap justify-center gap-4 mt-12">
        {data.buttons.map((button, index) => (
          <ButtonLink
            key={index}
            url={button.url}
            target={button.target}
            className="px-6 py-3 font-semibold rounded-lg transition-all duration-200 hover:opacity-90 hover:shadow-md inline-block"
            style={getButtonStyles(button.style)}
          >
            {button.text}
          </ButtonLink>
        ))}
      </div>
    );
  };

  const toggleItem = (itemId: string) => {
    setOpenItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        if (!data.allowMultipleOpen) {
          // Close all other items if multiple open is not allowed
          newSet.clear();
        }
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const sortedItems = [...(data.items || [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Determine if background is a gradient or solid color
  const isGradient = backgroundColor.includes('gradient');
  const backgroundStyle = isGradient ? { background: backgroundColor } : { backgroundColor };

  if (data.layout === 'grid') {
    return (
      <section className="py-16 md:py-24" style={backgroundStyle}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: titleColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
            />
            {data.subtitle && (
              <div
                className="rich-text-content text-lg md:text-xl"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>

          {/* Grid Layout */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {sortedItems.map((item, index) => (
              <div
                key={item.id}
                className="p-6 rounded-lg border"
                style={{
                  backgroundColor: itemBackground,
                  borderColor: itemBorder,
                }}
              >
                <div className="flex items-start gap-3">
                  {data.showNumbers && (
                    <span
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{ backgroundColor: iconColor, color: '#ffffff' }}
                    >
                      {index + 1}
                    </span>
                  )}
                  <div>
                    <h3
                      className="text-lg font-semibold mb-2"
                      style={{ color: questionColor, ...headingFontStyle }}
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.question) }}
                    />
                    <div
                      className="rich-text-content leading-relaxed"
                      style={{ color: answerColor, ...bodyFontStyle }}
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.answer) }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call-to-Action Buttons */}
          {renderButtons()}
        </div>
      </section>
    );
  }

  if (data.layout === 'list') {
    return (
      <section className="py-16 md:py-24" style={backgroundStyle}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section Header */}
          <div className="text-center mb-12">
            <h2
              className="text-3xl md:text-4xl font-bold mb-4"
              style={{ color: titleColor, ...headingFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
            />
            {data.subtitle && (
              <div
                className="rich-text-content text-lg md:text-xl"
                style={{ color: subtitleColor, ...bodyFontStyle }}
                dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
              />
            )}
          </div>

          {/* List Layout - All items visible */}
          <div className="space-y-6">
            {sortedItems.map((item, index) => (
              <div
                key={item.id}
                className="p-6 rounded-lg border"
                style={{
                  backgroundColor: itemBackground,
                  borderColor: itemBorder,
                }}
              >
                <div className="flex items-start gap-4">
                  {data.showNumbers && (
                    <span
                      className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold"
                      style={{ backgroundColor: iconColor, color: '#ffffff' }}
                    >
                      {index + 1}
                    </span>
                  )}
                  <div>
                    <h3
                      className="text-lg font-semibold mb-3"
                      style={{ color: questionColor, ...headingFontStyle }}
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.question) }}
                    />
                    <div
                      className="rich-text-content leading-relaxed"
                      style={{ color: answerColor, ...bodyFontStyle }}
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.answer) }}
                    />
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Call-to-Action Buttons */}
          {renderButtons()}
        </div>
      </section>
    );
  }

  // Default: Accordion layout
  return (
    <section className="py-16 md:py-24" style={backgroundStyle}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2
            className="text-3xl md:text-4xl font-bold mb-4"
            style={{ color: titleColor, ...headingFontStyle }}
            dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.title) }}
          />
          {data.subtitle && (
            <div
              className="rich-text-content text-lg md:text-xl"
              style={{ color: subtitleColor, ...bodyFontStyle }}
              dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(data.subtitle) }}
            />
          )}
        </div>

        {/* Accordion */}
        <div className="space-y-4">
          {sortedItems.map((item, index) => {
            const isOpen = openItems.has(item.id);
            return (
              <div
                key={item.id}
                className="rounded-lg border overflow-hidden transition-shadow duration-200 hover:shadow-md"
                style={{
                  backgroundColor: itemBackground,
                  borderColor: itemBorder,
                }}
              >
                <button
                  onClick={() => toggleItem(item.id)}
                  className={`w-full px-6 py-4 flex items-center ${data.iconPosition === 'left' ? 'gap-3' : 'justify-between'} text-left focus:outline-none`}
                  aria-expanded={isOpen}
                >
                  {data.iconPosition === 'left' && (
                    <span className="flex-shrink-0" style={{ color: iconColor }}>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </span>
                  )}
                  <div className="flex items-center gap-3">
                    {data.showNumbers && (
                      <span
                        className="flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-sm font-semibold"
                        style={{ backgroundColor: iconColor, color: '#ffffff' }}
                      >
                        {index + 1}
                      </span>
                    )}
                    <span
                      className="text-lg font-semibold pr-4"
                      style={{ color: questionColor, ...headingFontStyle }}
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.question) }}
                    />
                  </div>
                  {data.iconPosition === 'right' && (
                    <span className="flex-shrink-0" style={{ color: iconColor }}>
                      {isOpen ? (
                        <ChevronUp className="w-5 h-5" />
                      ) : (
                        <ChevronDown className="w-5 h-5" />
                      )}
                    </span>
                  )}
                </button>
                <div
                  className={`overflow-hidden transition-all duration-300 ease-in-out ${
                    isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'
                  }`}
                >
                  <div className="px-6 pb-4" style={{ color: answerColor, ...bodyFontStyle }}>
                    <div
                      className="rich-text-content leading-relaxed"
                      dangerouslySetInnerHTML={{ __html: renderTextWithLineBreaks(item.answer) }}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Call-to-Action Buttons */}
        {renderButtons()}
      </div>
    </section>
  );
};
